import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PenNib, MagicWand, TextT, YoutubeLogo, Article, Trash, DownloadSimple } from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import analytics from "@/lib/analytics";

const ICONS = { paste: TextT, youtube: YoutubeLogo, upload: Article };

export default function Library() {
  const navigate = useNavigate();
  const { activeId } = useWorkspace();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState(null);

  useEffect(() => {
    if (!activeId) return;
    setLoading(true);
    api.get(`/content?workspace_id=${activeId}`).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [activeId]);

  const exportPack = async (item, e) => {
    e.stopPropagation();
    setExportingId(item.id);
    toast.info("Building your pack… clips can take a minute");
    try {
      const res = await api.get(`/content/${item.id}/export`, { responseType: "blob", timeout: 300000 });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const safe = (item.title || "pack").replace(/[^a-zA-Z0-9]+/g, "-").slice(0, 40);
      a.href = blobUrl; a.download = `${safe}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Pack downloaded");
      analytics.capture("pack_exported", { source: "library" });
    } catch { toast.error("Export failed"); }
    finally { setExportingId(null); }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this content pack and all its posts, visuals and audio?")) return;
    try {
      await api.delete(`/content/${id}`);
      setItems((it) => it.filter((c) => c.id !== id));
      toast.success("Content pack deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">Content Library</h1>
          <p className="text-muted-foreground mt-2">Everything you've repurposed in this workspace.</p>
        </div>
        <Button data-testid="library-new" onClick={() => navigate("/studio")}>
          <MagicWand size={18} weight="fill" className="mr-2" /> New Repurpose
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-16 text-center">
          <PenNib size={40} className="mx-auto text-muted-foreground mb-4" />
          <p className="font-heading font-bold text-lg">No content yet</p>
          <p className="text-muted-foreground mt-1 mb-6">Repurpose your first piece to get started.</p>
          <Button data-testid="empty-new" onClick={() => navigate("/studio")}><MagicWand size={18} weight="fill" className="mr-2" /> Start repurposing</Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((c, i) => {
            const Icon = ICONS[c.source_type] || TextT;
            return (
              <motion.div
                key={c.id}
                data-testid={`library-item-${c.id}`}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                onClick={() => navigate(`/studio/${c.id}`)}
                className="relative cursor-pointer text-left rounded-lg border border-border bg-card p-5 hover:-translate-y-0.5 hover:border-primary/40 transition-all"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-10 h-10 rounded-md bg-secondary flex items-center justify-center"><Icon size={18} /></div>
                  <div className="flex items-center gap-2">
                    {c.repurposed
                      ? <Badge variant="secondary">Repurposed</Badge>
                      : <Badge variant="outline">Draft</Badge>}
                    {c.repurposed && (
                      <button data-testid={`library-export-${c.id}`} onClick={(e) => exportPack(c, e)} disabled={exportingId === c.id}
                        className="text-muted-foreground hover:text-primary transition-colors p-1 disabled:opacity-50" title="Download pack (.zip)">
                        <DownloadSimple size={16} className={exportingId === c.id ? "animate-pulse" : ""} />
                      </button>
                    )}
                    <button data-testid={`library-delete-${c.id}`} onClick={(e) => remove(c.id, e)}
                      className="text-muted-foreground hover:text-destructive transition-colors p-1" title="Delete pack">
                      <Trash size={16} />
                    </button>
                  </div>
                </div>
                <h3 className="font-heading font-bold text-lg leading-snug line-clamp-2">{c.title}</h3>
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{c.content}</p>
                <p className="text-xs font-mono text-muted-foreground mt-4">
                  {new Date(c.created_at).toLocaleDateString()}
                </p>
              </motion.div>
            );
          })}
        </div>
      )}
    </AppLayout>
  );
}
